import { Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LunarApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/lunar/lunar-api';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { LunarDayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/lunar/lunar-day.response';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { PremiumTeaserComponent } from '../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../@commons/@toasts/premium-toast/premium-toast.component';
import { PremiumService } from '../@commons/@services/front/premium.service';
import { LockIconComponent } from '../@icons/lock-icon.component';

/** Les quatre familles de jours, dans l'ordre du cycle. */
const INFLUENCES = ['leaf', 'flower', 'fruit', 'root'] as const;
type Influence = (typeof INFLUENCES)[number];

/** Événements qui font un jour de repos — mêmes valeurs que le back. */
const NEUTRAL_EVENTS = [
  'ascending_node',
  'descending_node',
  'perigee',
  'apogee',
];

interface DayView {
  date: string;
  weekday: string;
  dayNumber: string;
  influence: Influence | null;
  moonPath: string;
  events: string[];
  neutral: boolean;
  isToday: boolean;
  /** Masqué pour un compte gratuit : seul aujourd'hui est visible. */
  locked: boolean;
}

/**
 * Détail du calendrier lunaire sur une semaine.
 *
 * Ce que la carte d'accueil ne peut pas porter : la suite des jours, ce que
 * chaque famille recouvre, et surtout les gestes à faire — la chip « jour
 * racines » ne dit rien à qui ne connaît pas la tradition.
 *
 * Gratuit : le jour même, depuis le payload des conseils (déjà chargé par
 * l'accueil). Les six suivants sont verrouillés. L'endpoint calendrier est
 * réservé aux abonnés, on ne l'appelle donc pas pour rien.
 */
@Component({
  selector: 'vw-lunar-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HeaderPaddingSafeAreaComponent,
    SkeletonLoaderComponent,
    PremiumTeaserComponent,
    PremiumToastComponent,
    LockIconComponent,
  ],
  templateUrl: './lunar-detail.component.html',
  providers: [
    { provide: LunarApi, useClass: LunarApi },
    { provide: AdviceApi, useClass: AdviceApi },
  ],
})
export class LunarDetailComponent {
  readonly premiumService = inject(PremiumService);
  private readonly lunarApi = inject(LunarApi);
  private readonly adviceApi = inject(AdviceApi);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);

  days: DayView[] = [];
  selected: DayView | null = null;
  advice: AdviceTodayResponse | null = null;
  isLoading = true;
  premiumPaywallOpen = false;

  /** Les quatre familles, pour la légende. */
  readonly influences = INFLUENCES;

  /** Mode déjà chargé, pour ne pas recharger deux fois la même chose. */
  private loadedFor: boolean | null = null;

  constructor() {
    // Comme la carte du calendrier : l'abonnement peut n'être connu qu'après
    // l'ouverture de l'écran. On recharge dès qu'il bascule, au lieu de figer
    // la vue sur la valeur lue au premier instant.
    effect(() => {
      const premium = this.premiumService.isPremium();
      if (this.loadedFor === premium) return;
      this.loadedFor = premium;
      void this.load();
    });
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    try {
      // Le payload des conseils sert dans les deux cas : il porte le jour
      // courant, le compteur de cultures concernées et les semis.
      this.advice = await this.adviceApi.getToday();
      this.days = this.premiumService.isPremium()
        ? await this.loadWeek()
        : this.buildFreeWeek();
      // `?day=` vient de la carte du calendrier : elle a annoncé un jour
      // précis, l'écran doit s'ouvrir dessus.
      const asked = this.route.snapshot.queryParamMap.get('day');
      this.selected =
        (asked ? this.days.find((d) => d.date === asked && !d.locked) : null) ??
        this.days.find((d) => d.isToday) ??
        this.days[0] ??
        null;
    } finally {
      this.isLoading = false;
    }
  }

  goBack(): void {
    this.location.back();
  }

  select(day: DayView): void {
    if (day.locked) {
      this.premiumPaywallOpen = true;
      return;
    }
    this.selected = day;
  }

  /** Cultures de l'utilisateur que le jour affiché concerne. */
  get matchedCount(): number {
    return this.advice?.lunar?.matchedPlantsCount ?? 0;
  }

  get sowingSuggestions() {
    return this.advice?.sowingSuggestions ?? [];
  }

  /** Prochain jour d'une influence donnée, pour la ligne de synthèse. */
  nextDayOf(influence: Influence): DayView | null {
    return (
      this.days.find(
        (d) => !d.isToday && !d.locked && !d.neutral && d.influence === influence,
      ) ?? null
    );
  }

  private async loadWeek(): Promise<DayView[]> {
    try {
      const calendar = await this.lunarApi.getWeek();
      return (calendar?.items ?? []).map((item) => this.toView(item, false));
    } catch {
      // 403 inattendu, réseau, adresse sans coordonnées : on retombe sur la
      // vue gratuite plutôt que d'afficher une erreur rouge.
      return this.buildFreeWeek();
    }
  }

  /**
   * Semaine du compte gratuit : le jour courant est réel (il vient du payload
   * des conseils), les six suivants sont des silhouettes verrouillées.
   */
  private buildFreeWeek(): DayView[] {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const iso = date.toISOString().slice(0, 10);
      if (i === 0) {
        return this.toView(
          {
            date: iso,
            influence: this.advice?.lunar?.influence,
            moonPath: this.advice?.lunar?.moonPath,
            events: this.advice?.lunar?.events ?? [],
          } as any,
          false,
        );
      }
      return this.toView({ date: iso, events: [] } as any, true);
    });
  }

  private toView(item: Partial<LunarDayResponse>, locked: boolean): DayView {
    const date = new Date(item.date + 'T12:00:00');
    const events = (item.events as unknown as string[]) ?? [];
    return {
      date: item.date,
      weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
      dayNumber: String(date.getDate()),
      influence: (item.influence as Influence) ?? null,
      moonPath: (item.moonPath as string) ?? '',
      events,
      neutral: events.some((e) => NEUTRAL_EVENTS.includes(e)),
      isToday: item.date === new Date().toISOString().slice(0, 10),
      locked,
    };
  }
}
